import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import axios from "axios";
import TypeIcon from "../Helpers/TypeIcon";
import TimeSince from "../Helpers/TimeSince";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoltLightning } from "@fortawesome/pro-regular-svg-icons";
import ProfilePicture from "../Auth/ProfilePicture";
import {
    faEye,
    faPaperclip,
    faEnvelopesBulk,
    faInfoCircle,
    faCommentSmile,
    faCommentXmark,
    faRobot,
    faCircleNotch,
    faCircleExclamation,
    faCheck,
    faHourglassStart,
    faMoneyBillWave,
    faCode,
    faRotateRight,
} from "@fortawesome/pro-regular-svg-icons";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { openModal } from "../../store/modal/modalReducer";
import { faHeartPulse, faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

// Helper function to check if error is 131026 (Message Undeliverable)
function isError131026(message) {
    if (!message.status_response) return false;

    try {
        const statusData = typeof message.status_response === "string" ? JSON.parse(message.status_response) : message.status_response;

        if (statusData?.status?.errors) {
            return statusData.status.errors.some((error) => error.code === 131026);
        }
    } catch (e) {
        // Not valid JSON or no errors
    }

    return false;
}

// Helper function to convert URLs in text to clickable links and img: prefixed URLs to images
function linkifyText(text) {
    if (!text) return text;

    // First, strip any existing anchor tags to avoid double-wrapping
    // This removes <a href="..." target="..."> and </a> tags
    text = text.replace(/<a\s+[^>]*href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/gi, "$1");

    // Process img: prefixed URLs FIRST (before regular URL linkification)
    // This prevents img: URLs from being converted to links
    const imgPlaceholders = [];
    text = text.replace(/img:(https?:\/\/[^\s"<>]+)/g, (match, url) => {
        // Remove trailing punctuation
        url = url.replace(/[.,;:!?]+$/, "");
        const placeholder = `__IMG_PLACEHOLDER_${imgPlaceholders.length}__`;
        imgPlaceholders.push(`<img src="${url}" style="max-width: 200px; margin: 5px 0; border-radius: 4px;" alt="Gallery image" />`);
        return placeholder;
    });

    // URL regex pattern - matches http://, https://, and www. URLs
    // Updated to not match URLs ending with quotes or angle brackets
    const urlPattern = /(https?:\/\/[^\s"<>]+)|(www\.[^\s"<>]+)/g;

    text = text.replace(urlPattern, (url) => {
        // Remove trailing punctuation that might be part of the sentence
        url = url.replace(/[.,;:!?]+$/, "");

        // Add https:// to www. URLs
        const href = url.startsWith("www.") ? `https://${url}` : url;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${url}</a>`;
    });

    // Restore img tags from placeholders
    imgPlaceholders.forEach((img, index) => {
        text = text.replace(`__IMG_PLACEHOLDER_${index}__`, img);
    });

    return text;
}

export default function BookingMessages({ messages, setMsgCount, totalMessages, loadedMessages, showMemoryWarning, setShowMemoryWarning, hasMoreMessages }) {
    const messagesEndRef = useRef(null);
    const dispatch = useDispatch();
    const history = useHistory();
    const { current_booking } = useSelector((state) => state.booking);
    const { staff = [], current_staff } = useSelector((state) => state.auth);

    // Define fixed message types
    const messageTypes = [
        { value: "audit", label: "Audit" },
        { value: "email", label: "Email" },
        { value: "note", label: "Note" },
        { value: "whatsapp", label: "Whatsapp" },
        { value: "appointment_comments", label: "Appointment Comments" },
    ];

    // Default: every message type ticked except Audit, unless the user's
    // "Show Audit Messages" setting (backend staff admin) is enabled.
    const [platformFilter, setPlatformFilter] = useState(
        current_staff?.show_audit_messages
            ? messageTypes.map((t) => t.value)
            : messageTypes.filter((t) => t.value !== "audit").map((t) => t.value)
    );
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [showApiResponse, setShowApiResponse] = useState(null); // Track which message's API response to show
    const [dateRange, setDateRange] = useState({
        start: "",
        end: "",
    });
    const isImage = (filePath) => /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(filePath);
    const isPDF = (filePath) => /\.pdf$/i.test(filePath);
    const isExcel = (filePath) => /\.xlsx$/i.test(filePath);
    const isVideo = (filePath) => /\.(mp4|avi|mkv|mov|wmv|flv|webm)$/i.test(filePath);

    // Known message types whose checkbox is currently unticked — their messages are hidden.
    // Platforms without a checkbox (e.g. sms, website, system) are always shown.
    const hiddenTypes = messageTypes
        .map((t) => t.value)
        .filter((value) => !platformFilter.includes(value));

    // Filter messages based on selected platform and search term
    const filteredMessages = messages.filter((message) => {
        const matchesPlatform = !hiddenTypes.some((filter) => {
            if (filter === "whatsapp") {
                return message.platform === "whatsapp" || message.platform === "whatsapp bot";
            }
            return message.platform === filter;
        });

        const matchesSearch =
            !searchTerm ||
            message.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            message.email_subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            message.response?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            message.template_name?.toLowerCase().includes(searchTerm.toLowerCase());

        // Check if message is within date range
        let matchesDateRange = true;
        if (dateRange.start || dateRange.end) {
            const messageDate = new Date(message.timestamp);
            const startDate = dateRange.start ? new Date(dateRange.start) : null;
            const endDate = dateRange.end ? new Date(dateRange.end) : null;

            if (startDate) startDate.setHours(0, 0, 0, 0);
            if (endDate) endDate.setHours(23, 59, 59, 999);

            if (startDate && messageDate < startDate) matchesDateRange = false;
            if (endDate && messageDate > endDate) matchesDateRange = false;
        }

        return matchesPlatform && matchesSearch && matchesDateRange;
    });

    // detect screensize, we don't want to scroll to bottom on mobile
    const [width, setWidth] = useState(window.innerWidth);

    function handleWindowSizeChange() {
        setWidth(window.innerWidth);
    }

    function MessageColour(id, platform, collection, hijackedUserId = null) {
        if (hijackedUserId) {
            return "bg-yellow-100"; // Light yellow background for hijacked messages
        } else if (platform === "note") {
            return "bg-yellow-200";
        } else if (platform === "audit") {
            return "bg-red-200";
        } else if (platform === "appointment_comments") {
            return "bg-teal-200";
        } else if (platform === "failed") {
            return "bg-red-200";
        } else if (platform === "website") {
            return "bg-green-200";
        } else if (platform === "failed") {
            return "bg-red-300";
        } else if ((Array.isArray(staff) && staff.find((e) => e.id === id)) || id == "-1" || id == "-2") {
            if (platform === "whatsapp bot") {
                return "bg-orange-200";
            } else if (collection === 1) {
                return "bg-purple-200";
            } else {
                return "bg-blue-200";
            }
        } else {
            return "bg-gray-200";
        }
    }

    function BorderColour(platform, collection, broker) {
        if (platform === "failed") {
            return "border-2 border-red-600";
        } else {
            if (collection === 1) {
                return "border-2 border-purple-600";
            } else if (collection === 2) {
                return "border-2 border-sky-500";
            } else if (broker === 1) {
                return "border-2 border-rose-400";
            } else {
                return "";
            }
        }
    }

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        window.addEventListener("resize", handleWindowSizeChange);
        if (width > 1200) scrollToBottom();
        return () => {
            window.removeEventListener("resize", handleWindowSizeChange);
        };
    }, [messages]);

    const getFilePath = (fullPath) => {
        if (!fullPath) return { filename: "", isDirectLink: false };

        // Safely construct a URL object. If fullPath is relative, provide a base.
        let url;
        try {
            url = new URL(fullPath, window.location.origin);
        } catch (e) {
            // As a last resort, treat as plain filename
            return { filename: String(fullPath).split("/").pop(), isDirectLink: false };
        }

        // Check if R2_DEVURL is defined or if it's the new format (contains /api/public/file/download)
        if (process.env.REACT_APP_R2_DEVURL || url.pathname.includes("/api/public/file/download")) {
            return {
                url: url.toString(),
                isDirectLink: true,
                filename: url.searchParams.get("path")?.split("/").pop() || "download",
            };
        }

        // For old format (/storage/), return the filename
        return {
            filename: url.pathname.split("/").pop(),
            isDirectLink: false,
        };
    };

    // console.log("current_booking", current_booking)
    async function handleDeleteMessage(id) {
        if (!current_staff || !current_staff.show_dev_stuff) return;
        if (!window.confirm("Are you sure you want to delete this message?")) return;

        try {
            await axios({ method: "delete", url: `${process.env.REACT_APP_API_URL}message/${id}` });
            if (typeof setMsgCount === "function") {
                setMsgCount((prev) => (typeof prev === "number" ? Math.max(prev - 1, 0) : prev));
            }
        } catch (e) {
            console.error("Failed to delete message", e);
            alert("Failed to delete message");
        }
    }

    async function handleRetryMessage(id) {
        if (!window.confirm("Retry sending this message? A new message will be created at the bottom of the chat.")) return;

        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}messages/${id}/retry`,
            });

            if (response.data.success) {
                alert("Message retry queued successfully! The new message will appear shortly.");
            } else {
                alert("Failed to retry message: " + (response.data.message || "Unknown error"));
            }
        } catch (e) {
            console.error("Failed to retry message", e);
            alert("Failed to retry message: " + (e.response?.data?.message || e.message));
        }
    }

    return (
        <div className="overflow-y-auto scroll-smooth w-full h-[500px] md:h-[600px] xl:h-full">
            {current_booking && (
                <>
                    {/* Filter Toggle Button and Bar */}
                    <div className="sticky top-0 z-10">
                        <div className="relative">
                            {/* Red Triangle for Large Chat Warning */}
                            {hasMoreMessages && !showMemoryWarning && (
                                <button
                                    onClick={() => setShowMemoryWarning(true)}
                                    className="absolute top-2 left-14 z-20 m-1 flex items-center justify-center w-8 h-8 bg-red-600 hover:bg-red-700 rounded-full shadow-lg transition-colors"
                                    title="Show large chat warning"
                                >
                                    <FontAwesomeIcon icon={faTriangleExclamation} className="w-4 h-4 text-white" />
                                </button>
                            )}
                            
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`absolute top-2 left-2 z-20 m-1 p-1.5 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
                                    platformFilter.length > 0 || searchTerm || dateRange.start || dateRange.end
                                        ? "bg-blue-500 text-white hover:bg-blue-600"
                                        : "bg-white text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                                }`}
                                title={showFilters ? "Hide filters" : "Show filters"}
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                    />
                                </svg>
                            </button>

                            {/* Compact Filter Bar */}
                            <div className={`bg-white border-b border-gray-200 shadow-sm transition-all duration-200 ease-in-out overflow-hidden ${showFilters ? "max-h-60 py-3 pl-12" : "max-h-0 py-0"}`}>
                                <div className="px-4 space-y-3">
                                    {/* Message Count and Types Row */}
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-2 flex-wrap flex-1">
                                            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Message Types:</label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (platformFilter.length === messageTypes.length) {
                                                    setPlatformFilter([]);
                                                } else {
                                                    setPlatformFilter(messageTypes.map(t => t.value));
                                                }
                                            }}
                                            className="flex items-center gap-1 px-2 py-0.5 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                            title={platformFilter.length === messageTypes.length ? "Deselect all" : "Select all"}
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                {platformFilter.length === messageTypes.length ? (
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                ) : (
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                )}
                                            </svg>
                                            {platformFilter.length === messageTypes.length ? "Clear" : "All"}
                                        </button>
                                        {messageTypes.map((type) => (
                                            <label key={type.value} className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 px-1.5 py-0.5 rounded transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={platformFilter.includes(type.value)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setPlatformFilter([...platformFilter, type.value]);
                                                        } else {
                                                            setPlatformFilter(platformFilter.filter(f => f !== type.value));
                                                        }
                                                    }}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                                                />
                                                <span className="text-xs text-gray-700">{type.label}</span>
                                            </label>
                                        ))}
                                        </div>
                                        
                                        {/* Message Count */}
                                        {messages.length > 0 && (
                                            <div className="flex-shrink-0 px-3 py-1 bg-blue-50 border border-blue-200 rounded-md">
                                                <span className="text-xs font-semibold text-blue-800">
                                                    {filteredMessages.length.toLocaleString()} / {messages.length.toLocaleString()}
                                                </span>
                                                <span className="text-xs text-blue-600 ml-1">messages</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Search and Date Range Row */}
                                    <div className="flex items-center gap-4 pt-1 border-t border-gray-100">
                                        <div className="relative flex-1 max-w-xs">
                                            <input
                                                type="text"
                                                className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md bg-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Search..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                            {searchTerm ? (
                                                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setSearchTerm("")}>
                                                    <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            ) : (
                                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <input
                                                type="date"
                                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                value={dateRange.start}
                                                onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                                            />
                                            <span className="text-sm text-gray-500 font-medium">to</span>
                                            <input
                                                type="date"
                                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                value={dateRange.end}
                                                min={dateRange.start}
                                                onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                                            />
                                            {(dateRange.start || dateRange.end) && (
                                                <button type="button" className="text-gray-400 hover:text-gray-600 p-1.5 hover:bg-gray-100 rounded transition-colors" onClick={() => setDateRange({ start: "", end: "" })} title="Clear date range">
                                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-y-auto flex-col-reverse">
                        {filteredMessages.length > 0 ? (
                            filteredMessages.map((d) => {
                                return (
                                    <div
                                        key={d.id}
                                        className={classNames(
                                            (Array.isArray(staff) && staff.find((e) => e.id === parseInt(d.from))) || d.from == -1 || d.from == -2 ? "lg:justify-end lg:ml-24" : "lg:justify-start lg:mr-24",
                                            "flex py-4",
                                        )}
                                    >
                                        <div
                                            className={classNames(
                                                (Array.isArray(staff) && staff.find((e) => e.id === parseInt(d.from))) || d.from == -1 || d.from == -2 ? "" : "flex-row-reverse",
                                                "flex ",
                                            )}
                                        >
                                            <div className="relative">
                                                {/* Hijacked message indicator - show for both regular and system messages */}
                                                {d.hijacked_user_id > 0 && (
                                                    <div className="absolute -top-2 left-0">
                                                        <div className="bg-yellow-200 text-yellow-800 text-xs px-3 py-0.5 rounded-full flex items-center whitespace-nowrap">
                                                            <FontAwesomeIcon icon={faBoltLightning} className="w-3 h-3 mr-1.5" />
                                                            {d.hijacked_user ? (
                                                                <>
                                                                    Hijacked by{" "}
                                                                    <a
                                                                        href={`${process.env.REACT_APP_BACKEND_URL || "http://romi-backend.test"}/admin/tpn-approvals/${d.hijacked_user_id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="font-semibold underline hover:text-yellow-900 ml-1"
                                                                        title="View all hijacked chats by this admin"
                                                                    >
                                                                        {d.hijacked_user.name} {d.hijacked_user.surname || ""}
                                                                    </a>
                                                                </>
                                                            ) : (
                                                                "Hijacked"
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                                {(d.booking_id === null || d.booking_id === undefined || d.booking_id === "" || d.booking_id === 0) && (
                                                    <div className="absolute -top-2 right-0">
                                                        <div className="bg-red-200 text-red-800 text-xs px-2 py-0.5 rounded-full whitespace-nowrap" title="Message is missing booking_id">
                                                            Missing booking ID
                                                        </div>
                                                    </div>
                                                )}
                                                <div
                                                    className={classNames(
                                                        MessageColour(parseInt(d.from), d.platform, d.collection, d.hijacked_user_id),
                                                        "p-4 rounded-md text-sm lg:max-w-sm break-words mt-2",
                                                        BorderColour(d.platform, d.collection, d.broker),
                                                    )}
                                                >
                                                    {/* Display contact name if message is from an additional contact */}
                                                    {d.contact_id && d.contact && (
                                                        <div className="text-xs font-semibold text-gray-600 mb-2 pb-2 border-b border-gray-300">
                                                            From: {d.contact.name || d.contact.number}
                                                        </div>
                                                    )}
                                                    {d.to == "27818644206" && (
                                                        <div className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-1 rounded mb-2 border border-amber-300">
                                                            ⚠ Sent to old QL number
                                                        </div>
                                                    )}
                                                    {d.response !== undefined && d.response !== false ? <p className="italic font-semibold underline mb-2">{d.response}</p> : ""}
                                                    {d.platform === "appointment_comments" && d.appointment ? (
                                                        <div 
                                                            className="text-xs mb-2 cursor-pointer hover:bg-teal-300 p-2 rounded transition-colors"
                                                            onClick={() => dispatch(
                                                                openModal({
                                                                    modal_type: "ModalViewAppointment",
                                                                    modal_props: {
                                                                        booking_id: d.row_id,
                                                                        property_id: d.appointment.property_id
                                                                    }
                                                                })
                                                            )}
                                                            title="Click to view appointment"
                                                        >
                                                            <div className="font-semibold">📅 Appointment Comment</div>
                                                            <div><strong>Type:</strong> {d.appointment.appointment?.name || 'N/A'}</div>
                                                            <div className="mb-2"><strong>Date:</strong> {d.appointment.booked_date ? new Date(d.appointment.booked_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</div>
                                                            <div>{d.message}</div>
                                                        </div>
                                                    ) : null}
                                                    {d.platform === "email" && d.email_subject != null && d.email_subject !== "" && (
                                                        <div className="font-bold mb-4">{d.email_subject && "Subject: " + d.email_subject}</div>
                                                    )}
                                                    {(d.template_name || d.is_bulk_message === 1 || parseInt(d?.bulk_action_id) > 0) && (
                                                        <p className="font-bold mb-4 flex items-center gap-1.5">
                                                            {d.template_name}
                                                            {(d.is_bulk_message === 1 || parseInt(d?.bulk_action_id) > 0) && (
                                                                <span className="group relative inline-flex">
                                                                    <FontAwesomeIcon icon={faEnvelopesBulk} className="text-blue-600 text-xs cursor-help" />
                                                                    <span className="invisible group-hover:visible absolute left-0 top-5 z-50 w-40 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-normal">
                                                                        {parseInt(d?.bulk_action_id) > 0 ? `Bulk Message · Action #${d.bulk_action_id}` : "Bulk Message"}
                                                                    </span>
                                                                </span>
                                                            )}
                                                            {d.is_retry && (
                                                                <span className="group relative inline-flex">
                                                                    <FontAwesomeIcon icon={faRotateRight} className="text-green-600 text-xs cursor-help" />
                                                                    <span className="invisible group-hover:visible absolute left-0 top-5 z-50 w-32 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-normal">
                                                                        Retried Message
                                                                    </span>
                                                                </span>
                                                            )}
                                                            {d.sent_as_normal === 1 && (
                                                                <span className="group relative inline-flex">
                                                                    <FontAwesomeIcon icon={faMoneyBillWave} className="text-green-600 text-xs cursor-help" />
                                                                    <span className="invisible group-hover:visible absolute left-0 top-5 z-50 w-48 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-normal">
                                                                        Sent as normal message (within 24hr window - cost saved)
                                                                    </span>
                                                                </span>
                                                            )}
                                                        </p>
                                                    )}
                                                    <pre className="whitespace-pre-wrap break-words max-w-full overflow-auto">
                                                        {/* Property24 emails: display as plain text with formatting */}
                                                        {d.message_html &&
                                                        (d.message_html.includes("property24.com") || d.message_html.includes("Property24")) &&
                                                        d.message_html.includes("LISTING ENQUIRY") ? (
                                                            (() => {
                                                                // Extract clean text from Property24 HTML
                                                                const extractProperty24Text = (html) => {
                                                                    // Helper to extract text after a label, handling various HTML structures
                                                                    const extractField = (label) => {
                                                                        // Try multiple patterns to handle different Property24 formats
                                                                        const patterns = [
                                                                            // Pattern 1: <strong>Label:</strong> </td> <td>Value</td>
                                                                            new RegExp(`<strong>${label}:<\\/strong>\\s*<\\/td>\\s*<td[^>]*>([^<]+)<\\/td>`, "i"),
                                                                            // Pattern 2: Label: \n Value (plain text with whitespace)
                                                                            new RegExp(`${label}:\\s*\\n\\s*([^\\n<]+)`, "i"),
                                                                            // Pattern 3: Label: Value (inline)
                                                                            new RegExp(`${label}:\\s*([^\\n<]+)`, "i"),
                                                                        ];

                                                                        for (const pattern of patterns) {
                                                                            const match = html.match(pattern);
                                                                            if (match && match[1]) {
                                                                                return match[1].trim();
                                                                            }
                                                                        }
                                                                        return null;
                                                                    };

                                                                    // Special handling for contact number (may have WhatsApp link)
                                                                    const extractContact = () => {
                                                                        const patterns = [
                                                                            /Contact Number:\s*<\/td>\s*<td[^>]*>.*?([0-9\s]+)\s*\(/is,
                                                                            /Contact Number:\s*\n\s*([0-9\s]+)\s*\(/is,
                                                                            /Contact Number:\s*([0-9\s]+)/i,
                                                                        ];

                                                                        for (const pattern of patterns) {
                                                                            const match = html.match(pattern);
                                                                            if (match && match[1]) {
                                                                                return match[1].trim().replace(/\s+/g, "");
                                                                            }
                                                                        }
                                                                        return null;
                                                                    };

                                                                    const lines = ["📧 Property24 Enquiry", ""];

                                                                    // Extract property details first (price, type, bedrooms, etc.)
                                                                    const address = extractField("Address");
                                                                    if (address) lines.push(`**Address:** ${address}`);

                                                                    // Extract price (look for R amount in various formats)
                                                                    // Try to find price near property type or in listing details
                                                                    let priceMatch = html.match(/R\s*([\d\s]+?)(?:\s*(?:Apartment|Flat|House|Townhouse|Studio|Room))/i);
                                                                    if (!priceMatch) {
                                                                        priceMatch = html.match(/R\s*([\d\s]{3,})/i); // At least 3 chars (handles "4 950")
                                                                    }
                                                                    if (priceMatch && priceMatch[1] && /\d/.test(priceMatch[1])) {
                                                                        const price = priceMatch[1].trim().replace(/\s+/g, " ");
                                                                        lines.push(`**Price:** R ${price}`);
                                                                    }

                                                                    // Extract property type
                                                                    const typeMatch = html.match(/(?:Apartment|Flat|House|Townhouse|Studio|Room)/i);
                                                                    if (typeMatch) {
                                                                        lines.push(`**Type:** ${typeMatch[0]}`);
                                                                    }

                                                                    // Extract bedrooms, bathrooms, parking
                                                                    const bedroomsMatch = html.match(/icon_bed[^>]*>[\s\n]*([0-9.]+)/i);
                                                                    const bathroomsMatch = html.match(/icon_bath[^>]*>[\s\n]*([0-9.]+)/i);
                                                                    const parkingMatch = html.match(/icon_parking[^>]*>[\s\n]*([0-9.]+)/i);

                                                                    const details = [];
                                                                    if (bedroomsMatch) details.push(`${bedroomsMatch[1]} bed`);
                                                                    if (bathroomsMatch) details.push(`${bathroomsMatch[1]} bath`);
                                                                    if (parkingMatch) details.push(`${parkingMatch[1]} parking`);
                                                                    if (details.length > 0) {
                                                                        lines.push(`**Details:** ${details.join(", ")}`);
                                                                    }

                                                                    // Extract web reference
                                                                    const webRef = extractField("Web ref");
                                                                    if (webRef) lines.push(`**Web Ref:** ${webRef}`);

                                                                    lines.push(""); // Empty line before contact details

                                                                    // Extract contact information
                                                                    const name = extractField("Enquiry by");
                                                                    if (name) lines.push(`**Enquiry by:** ${name}`);

                                                                    const contact = extractContact();
                                                                    if (contact) lines.push(`**Contact Number:** ${contact}`);

                                                                    const email = extractField("Email Address");
                                                                    if (email) {
                                                                        const cleanEmail = email
                                                                            .replace(/&#x27;/g, "'")
                                                                            .replace(/&amp;/g, "&")
                                                                            .replace(/&lt;/g, "<")
                                                                            .replace(/&gt;/g, ">")
                                                                            .replace(/&quot;/g, '"');
                                                                        lines.push(`**Email Address:** ${cleanEmail}`);
                                                                    }

                                                                    const message = extractField("Message");
                                                                    if (message) {
                                                                        lines.push("");
                                                                        lines.push(`**Message:** ${message}`);
                                                                    }

                                                                    return lines.join("\n");
                                                                };

                                                                // Use extracted text from message field if available, otherwise extract from HTML
                                                                const displayText = d.message && d.message.includes("📧 Property24 Enquiry") ? d.message : extractProperty24Text(d.message_html);

                                                                return (
                                                                    <div className="property24-message">
                                                                        {displayText.split("\n").map((line, idx) => {
                                                                            // Bold text between ** markers
                                                                            if (line.includes("**")) {
                                                                                const parts = line.split("**");
                                                                                return (
                                                                                    <div key={idx} className="mb-1">
                                                                                        {parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}
                                                                                    </div>
                                                                                );
                                                                            }
                                                                            // Regular line
                                                                            return line ? (
                                                                                <div key={idx} className="mb-1">
                                                                                    {linkifyText(line)}
                                                                                </div>
                                                                            ) : (
                                                                                <br key={idx} />
                                                                            );
                                                                        })}
                                                                    </div>
                                                                );
                                                            })()
                                                        ) : d.platform === "appointment_comments" && d.appointment ? (
                                                            // Don't render message text separately for appointment comments - already shown in header
                                                            ""
                                                        ) : (
                                                            <div
                                                                className="dangerousPFormat"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: (() => {
                                                                        // Check if we have message_html, or if message contains HTML tags
                                                                        let html = "";
                                                                        if (d.message_html && d.message_html !== "") {
                                                                            html = d.message_html;
                                                                        } else if (d.message && (d.message.includes("<") || d.message.includes(">"))) {
                                                                            // Fallback: message contains HTML but message_html is empty
                                                                            html = d.message;
                                                                        } else {
                                                                            // Plain text message
                                                                            return linkifyText(d.message);
                                                                        }

                                                                        // Process HTML content
                                                                        html = html.includes("blockquote") ? html.split("<blockquote")[0] : html;

                                                                        // Replace mention anchor tags with @username using regex
                                                                        // Match any <a> tag with class="wysiwyg-mention" and extract data-value
                                                                        html = html.replace(/<a\s+[^>]*class="wysiwyg-mention"[^>]*>.*?<\/a>/gi, (match) => {
                                                                            // Extract data-value from the matched anchor tag
                                                                            const dataValueMatch = match.match(/data-value="([^"]+)"/i);
                                                                            if (dataValueMatch && dataValueMatch[1]) {
                                                                                return "@" + dataValueMatch[1];
                                                                            }
                                                                            // Fallback: extract text content if no data-value
                                                                            const textMatch = match.match(/>([^<]+)</);
                                                                            return textMatch ? textMatch[1] : match;
                                                                        });

                                                                        // Remove blockquote remnants
                                                                        html = html.replace(/<blockquote[^>]*>\|.*$/g, "");

                                                                        // Parse to remove any other anchor tags (non-mentions)
                                                                        const parser = new DOMParser();
                                                                        const doc = parser.parseFromString(html, "text/html");
                                                                        const links = doc.querySelectorAll("a");
                                                                        links.forEach((link) => {
                                                                            const textNode = document.createTextNode(link.textContent);
                                                                            link.parentNode.replaceChild(textNode, link);
                                                                        });

                                                                        // Now apply linkifyText to convert URLs to clickable links
                                                                        return linkifyText(doc.body.innerHTML);
                                                                    })(),
                                                                }}
                                                            />
                                                        )}
                                                    </pre>
                                                    {/* Display interactive buttons/lists if available */}
                                                    {(() => {
                                                        try {
                                                            if (d.message_html && d.message_html.trim().startsWith("{")) {
                                                                const interactiveData = JSON.parse(d.message_html);

                                                                if (interactiveData.type === "button" && interactiveData.buttons) {
                                                                    return (
                                                                        <div className="mt-3 space-y-2">
                                                                            {interactiveData.buttons.map((btn, idx) => (
                                                                                <div key={idx} className="px-3 py-2 bg-gray-100 rounded-md text-sm border border-gray-300">
                                                                                    {btn.title}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                }

                                                                if (interactiveData.type === "list" && interactiveData.sections) {
                                                                    return (
                                                                        <div className="mt-3">
                                                                            <div className="text-xs font-semibold text-gray-600 mb-2">{interactiveData.button || "Choose an Option"}</div>
                                                                            {interactiveData.sections.map((section, sIdx) => (
                                                                                <div key={sIdx} className="mb-3">
                                                                                    {section.title && <div className="text-xs font-bold text-gray-700 mb-1">{section.title}</div>}
                                                                                    <div className="space-y-1">
                                                                                        {section.rows.map((row, rIdx) => (
                                                                                            <div key={rIdx} className="px-3 py-2 bg-gray-100 rounded-md text-sm border border-gray-300">
                                                                                                <div className="font-medium">{row.title}</div>
                                                                                                {row.description && <div className="text-xs text-gray-600 mt-1">{row.description}</div>}
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                }
                                                            }
                                                        } catch (e) {
                                                            // Not JSON, ignore
                                                        }
                                                        return null;
                                                    })()}
                                                    {d.failed_reason !== undefined && d.failed_reason !== null && d.failed_reason !== "" ? (
                                                        <>
                                                            <hr />
                                                            <div className="flex items-start gap-2 mt-2">
                                                                <p className="text-xs italic flex-1">{d.failed_reason}</p>
                                                                <div className="flex items-center gap-2">
                                                                    {/* Retry Button - only show for whatsapp and email platforms */}
                                                                    {(d.platform === "whatsapp" || d.platform === "whatsapp bot" || d.platform === "email") && (
                                                                        <button
                                                                            onClick={() => handleRetryMessage(d.id)}
                                                                            className="text-green-600 hover:text-green-800 transition-colors"
                                                                            title="Retry sending this message"
                                                                        >
                                                                            <FontAwesomeIcon icon={faRotateRight} className="text-sm" />
                                                                        </button>
                                                                    )}
                                                                    {/* Response Viewer Button - shows both webhook and initial API responses */}
                                                                    {(d.status_response || d.api_response) && (
                                                                        <button
                                                                            onClick={() => setShowApiResponse(showApiResponse === d.id ? null : d.id)}
                                                                            className="text-purple-600 hover:text-purple-800 transition-colors"
                                                                            title={(() => {
                                                                                const parts = [];
                                                                                if (d.status_response) parts.push("Webhook Response");
                                                                                if (d.api_response) parts.push("API Response");
                                                                                return `View ${parts.join(" + ")}`;
                                                                            })()}
                                                                        >
                                                                            <FontAwesomeIcon icon={faCode} className="text-sm" />
                                                                        </button>
                                                                    )}
                                                                    {isError131026(d) && (
                                                                        <div className="group relative">
                                                                            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 cursor-help text-sm" />
                                                                            <div className="invisible group-hover:visible absolute z-50 right-0 w-80 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg">
                                                                                <p className="font-semibold mb-2">Error 131026 - Message Undeliverable</p>
                                                                                <p className="mb-2">Possible reasons:</p>
                                                                                <ul className="list-disc list-inside space-y-1 ml-2">
                                                                                    <li>The recipient phone number is not a WhatsApp phone number</li>
                                                                                    <li>Recipient has not accepted WhatsApp's Terms of Service and Privacy Policy</li>
                                                                                    <li>Recipient using an old WhatsApp version</li>
                                                                                </ul>
                                                                                <p className="mt-2 text-gray-300">Required versions:</p>
                                                                                <ul className="text-gray-300 ml-2 mt-1">
                                                                                    <li>• Android: 2.21.15.15+</li>
                                                                                    <li>• iOS: 2.21.170.4+</li>
                                                                                    <li>• Web: 2.2132.6+</li>
                                                                                </ul>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {/* Response Display - Show webhook status_response (most accurate) or fallback to api_response */}
                                                            {showApiResponse === d.id && (d.status_response || d.api_response) && (
                                                                <div className="mt-2 space-y-2">
                                                                    {/* Webhook Status Response (preferred - actual delivery status) */}
                                                                    {d.status_response && (
                                                                        <div className="p-2 bg-gray-900 rounded text-xs overflow-x-auto">
                                                                            <div className="flex justify-between items-center mb-1">
                                                                                <span className="text-yellow-400 font-mono text-xs">Webhook Status Response (Actual Delivery Status):</span>
                                                                                <button
                                                                                    onClick={() => {
                                                                                        const textToCopy = (() => {
                                                                                            try {
                                                                                                const parsed =
                                                                                                    typeof d.status_response === "string" ? JSON.parse(d.status_response) : d.status_response;
                                                                                                return JSON.stringify(parsed, null, 2);
                                                                                            } catch (e) {
                                                                                                return typeof d.status_response === "string" ? d.status_response : JSON.stringify(d.status_response);
                                                                                            }
                                                                                        })();
                                                                                        navigator.clipboard.writeText(textToCopy);
                                                                                        alert("Copied to clipboard!");
                                                                                    }}
                                                                                    className="text-gray-400 hover:text-white text-xs px-2 py-1 rounded hover:bg-gray-800"
                                                                                >
                                                                                    Copy
                                                                                </button>
                                                                            </div>
                                                                            <pre className="text-green-400 font-mono text-xs whitespace-pre-wrap break-words">
                                                                                {(() => {
                                                                                    try {
                                                                                        const parsed = typeof d.status_response === "string" ? JSON.parse(d.status_response) : d.status_response;
                                                                                        return JSON.stringify(parsed, null, 2);
                                                                                    } catch (e) {
                                                                                        return typeof d.status_response === "string" ? d.status_response : JSON.stringify(d.status_response);
                                                                                    }
                                                                                })()}
                                                                            </pre>
                                                                        </div>
                                                                    )}

                                                                    {/* API Response (initial send response) */}
                                                                    {d.api_response && (
                                                                        <div className="p-2 bg-gray-900 rounded text-xs overflow-x-auto">
                                                                            <div className="flex justify-between items-center mb-1">
                                                                                <span className="text-gray-400 font-mono text-xs">API Response (Initial Send):</span>
                                                                                <button
                                                                                    onClick={() => {
                                                                                        const textToCopy = (() => {
                                                                                            try {
                                                                                                const parsed = typeof d.api_response === "string" ? JSON.parse(d.api_response) : d.api_response;
                                                                                                return JSON.stringify(parsed, null, 2);
                                                                                            } catch (e) {
                                                                                                return d.api_response;
                                                                                            }
                                                                                        })();
                                                                                        navigator.clipboard.writeText(textToCopy);
                                                                                        alert("Copied to clipboard!");
                                                                                    }}
                                                                                    className="text-gray-400 hover:text-white text-xs px-2 py-1 rounded hover:bg-gray-800"
                                                                                >
                                                                                    Copy
                                                                                </button>
                                                                            </div>
                                                                            <pre className="text-green-400 font-mono text-xs whitespace-pre-wrap break-words">
                                                                                {(() => {
                                                                                    try {
                                                                                        const parsed = typeof d.api_response === "string" ? JSON.parse(d.api_response) : d.api_response;
                                                                                        return JSON.stringify(parsed, null, 2);
                                                                                    } catch (e) {
                                                                                        return d.api_response;
                                                                                    }
                                                                                })()}
                                                                            </pre>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        ""
                                                    )}
                                                    {/* Show resend schedule/status info for this failed message */}
                                                    {(() => {
                                                        const hasResend = d.resend_status || d.resend_at;
                                                        if (!hasResend) return null;
                                                        const status = (d.resend_status || "").toLowerCase();
                                                        const when = d.resend_at && moment(d.resend_at).isValid() ? moment(d.resend_at).format("YYYY-MM-DD HH:mm") : null;
                                                        const reason = d.resend_reason || "";
                                                        if (status === "pending") {
                                                            return (
                                                                <p className="text-xs text-orange-600 mt-1">
                                                                    Auto-resend{" "}
                                                                    {when ? (
                                                                        <>
                                                                            <strong>scheduled for {when}</strong>
                                                                        </>
                                                                    ) : (
                                                                        "scheduled"
                                                                    )}
                                                                    {reason ? ` (${reason.replaceAll("_", " ")})` : ""}.
                                                                </p>
                                                            );
                                                        }
                                                        if (status === "sent") {
                                                            return (
                                                                <p className="text-xs text-green-700 mt-1">
                                                                    Auto-resend <strong>sent</strong>
                                                                    {when ? ` (original was scheduled for ${when})` : ""}.
                                                                </p>
                                                            );
                                                        }
                                                        if (status === "failed") {
                                                            return (
                                                                <p className="text-xs text-red-600 mt-1">
                                                                    Auto-resend <strong>failed</strong>
                                                                    {when ? ` (was scheduled for ${when})` : ""}.
                                                                </p>
                                                            );
                                                        }
                                                        if (status === "cancelled") {
                                                            return (
                                                                <p className="text-xs text-gray-600 mt-1">
                                                                    Auto-resend <strong>cancelled</strong>
                                                                    {when ? ` (was scheduled for ${when})` : ""}.
                                                                </p>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                    {/* Experiment block notice on the specific failed WhatsApp message that triggered it */}
                                                    {(() => {
                                                        if ((d.platform === "whatsapp" || d.platform === "whatsapp bot") && d.send_status && String(d.send_status).toLowerCase() === "failed") {
                                                            const fr = d.failed_reason || "";
                                                            const match = fr.match(/\[EXPERIMENT_BLOCK_UNTIL=([^\]]+)\]/);
                                                            if (match && match[1] && moment(match[1]).isValid()) {
                                                                const until = match[1];
                                                                return (
                                                                    <p className="text-xs text-red-600 mt-1">
                                                                        Experiment block until <strong>{moment(until).format("YYYY-MM-DD HH:mm")}</strong>
                                                                    </p>
                                                                );
                                                            }
                                                        }
                                                        return null;
                                                    })()}
                                                    {Array.isArray(staff) && staff.find((e) => e.id === parseInt(d.from)) && d.files.length > 0 && d.files != "" && (
                                                        <>
                                                            <p className="text-xs text-gray-800 mt-2">Uploaded the following file(s)</p>

                                                            {d.files instanceof Array ? (
                                                                d.files.map((file) => (
                                                                    <li key={file} className="text-xs text-gray-800">
                                                                        {file}
                                                                    </li>
                                                                ))
                                                            ) : (
                                                                <li className="text-xs text-gray-800">{d.files}</li>
                                                            )}
                                                        </>
                                                    )}{" "}
                                                    {Array.isArray(staff) && staff.find((e) => e.id === parseInt(d.from)) && d.file_path && (
                                                        <>
                                                            <p className="text-xs text-gray-800 mt-2">Uploaded the following file(s)</p>
                                                            {(() => {
                                                                const fileInfo = getFilePath(d.file_path);

                                                                if (fileInfo.isDirectLink) {
                                                                    return (
                                                                        <a
                                                                            href={fileInfo.url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="text-blue-500 underline"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            View {isPDF(d.file_path) ? "PDF" : isExcel(d.file_path) ? "Excel" : isVideo(d.file_path) ? "Video" : "File"}
                                                                        </a>
                                                                    );
                                                                }

                                                                const downloadUrl = `${process.env.REACT_APP_API_URL}public/file/download?path=${encodeURIComponent(fileInfo.filename)}`;

                                                                if (isPDF(d.file_path) || isExcel(d.file_path)) {
                                                                    return (
                                                                        <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                                                                            View {isPDF(d.file_path) ? "PDF" : "Excel"}
                                                                        </a>
                                                                    );
                                                                }

                                                                if (isImage(d.file_path)) {
                                                                    return (
                                                                        <img
                                                                            src={downloadUrl}
                                                                            alt="File uploaded"
                                                                            onError={(e) => {
                                                                                e.target.onerror = null; // Prevent infinite loop
                                                                                e.target.src = d.file_path; // Fallback to original path
                                                                            }}
                                                                        />
                                                                    );
                                                                }

                                                                return <p className="text-xs text-red-500">Unsupported file type</p>;
                                                            })()}
                                                        </>
                                                    )}
                                                    {!d.hijacked_user_id &&
                                                        d.from !== -1 &&
                                                        d.from != current_booking?.user?.cell &&
                                                        !(Array.isArray(staff) && staff.find((e) => e.id === parseInt(d.from))) &&
                                                        d.platform === "whatsapp" && (
                                                            <p className="text-xs text-red-500 mt-2 bold">
                                                                FROM SECOND NUMBER
                                                                <br />
                                                                If you get a 24 hour error please make this number the primary number.
                                                                <br />
                                                                {d.from}
                                                            </p>
                                                        )}
                                                </div>
                                                <div
                                                    className={classNames(
                                                        (Array.isArray(staff) && staff.find((e) => e.id === parseInt(d.from))) || d.from == -1 || d.from == -2 ? "justify-end" : "justify-start",
                                                        "flex items-center text-gray-900 text-xs",
                                                    )}
                                                >
                                                    <TypeIcon platform={d.platform} className="w-4 h-4 mr-2" />
                                                    {d.platform && d.platform === "email" && (
                                                        <FontAwesomeIcon
                                                            icon={faInfoCircle}
                                                            className="mx-1 cursor-pointer"
                                                            onClick={() =>
                                                                dispatch(
                                                                    openModal({
                                                                        modal_type: "ModalMailRawInfo",
                                                                        modal_props: {
                                                                            d,
                                                                        },
                                                                    }),
                                                                )
                                                            }
                                                        />
                                                    )}
                                                    <TimeSince time={d.timestamp} className="mr-2" />
                                                    <div
                                                        className={classNames(
                                                            (Array.isArray(staff) && staff.find((e) => e.id === parseInt(d.from))) || d.from == -1 || d.from == -2 ? "mr-auto" : "ml-auto",
                                                        )}
                                                    >
                                                        {d.files && d.files.length > 0 && d.files[0] > 0 && (
                                                            <FontAwesomeIcon
                                                                icon={faPaperclip}
                                                                className="hover:cursor-pointer hover:text-gray-500 mr-2"
                                                                title="View the attachments for more information"
                                                            />
                                                        )}
                                                        {d.files.length === 1 && d.files[0] > 0 && (
                                                            <FontAwesomeIcon
                                                                icon={faEye}
                                                                className="hover:cursor-pointer hover:text-gray-500"
                                                                title="View"
                                                                onClick={() =>
                                                                    dispatch(
                                                                        openModal({
                                                                            modal_type: "ModalViewFiles",
                                                                            modal_props: {
                                                                                booking_id: current_booking?.id,
                                                                                files: [
                                                                                    {
                                                                                        id: d.files[0],
                                                                                    },
                                                                                ],
                                                                            },
                                                                        }),
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </div>

                                                    {(d.platform === "whatsapp" || d.platform === "whatsapp bot" || d.platform === "email") &&
                                                    Array.isArray(staff) &&
                                                    staff.find((e) => e.id === parseInt(d.from) || d.from == -1 || d.from == -2) ? (
                                                        <>
                                                            {d.send_status === "Sending" ? (
                                                                <FontAwesomeIcon icon={faCircleNotch} spin className="text-lg ml-2" />
                                                            ) : d.send_status === "Sent" ? (
                                                                <button
                                                                    onClick={() =>
                                                                        d.conversation_id != null &&
                                                                        dispatch(
                                                                            openModal({
                                                                                modal_type: "ModalStatus",
                                                                                modal_props: { id: d.id },
                                                                            }),
                                                                        )
                                                                    }
                                                                    className={d.conversation_id != null ? "cursor-pointer hover:opacity-70" : ""}
                                                                    disabled={d.conversation_id == null}
                                                                    title={d.conversation_id != null ? "View message status details" : ""}
                                                                >
                                                                    <FontAwesomeIcon icon={faCheck} className="text-lg text-gray-400 ml-2" />
                                                                </button>
                                                            ) : d.send_status === "Delivered" ? (
                                                                <button
                                                                    onClick={() =>
                                                                        d.conversation_id != null &&
                                                                        dispatch(
                                                                            openModal({
                                                                                modal_type: "ModalStatus",
                                                                                modal_props: { id: d.id },
                                                                            }),
                                                                        )
                                                                    }
                                                                    className={d.conversation_id != null ? "cursor-pointer hover:opacity-70 flex items-center ml-2" : "flex items-center ml-2"}
                                                                    disabled={d.conversation_id == null}
                                                                    title={d.conversation_id != null ? "View message status details" : ""}
                                                                >
                                                                    <FontAwesomeIcon icon={faCheck} className="text-sm text-gray-400" />
                                                                    <FontAwesomeIcon icon={faCheck} className="text-sm text-gray-400 -ml-1" />
                                                                </button>
                                                            ) : d.send_status === "Read" ? (
                                                                <button
                                                                    onClick={() =>
                                                                        d.conversation_id != null &&
                                                                        dispatch(
                                                                            openModal({
                                                                                modal_type: "ModalStatus",
                                                                                modal_props: { id: d.id },
                                                                            }),
                                                                        )
                                                                    }
                                                                    className={d.conversation_id != null ? "cursor-pointer hover:opacity-70 flex items-center ml-2" : "flex items-center ml-2"}
                                                                    disabled={d.conversation_id == null}
                                                                    title={d.conversation_id != null ? "View message status details" : ""}
                                                                >
                                                                    <FontAwesomeIcon icon={faCheck} className="text-sm text-blue-500" />
                                                                    <FontAwesomeIcon icon={faCheck} className="text-sm text-blue-500 -ml-1" />
                                                                </button>
                                                            ) : d.send_status === "Failed" ? (
                                                                (d.failed_reason || d.status_response || d.api_response) ? (
                                                                    <button
                                                                        onClick={() => setShowApiResponse(showApiResponse === d.id ? null : d.id)}
                                                                        className="cursor-pointer hover:opacity-70 flex items-center ml-2"
                                                                        title="View error details and API response"
                                                                    >
                                                                        <FontAwesomeIcon icon={faCircleExclamation} className="text-lg text-red-500" />
                                                                    </button>
                                                                ) : (
                                                                    <div className="flex items-center ml-2" title="Message failed (no error details available)">
                                                                        <FontAwesomeIcon icon={faCircleExclamation} className="text-lg text-red-500" />
                                                                    </div>
                                                                )
                                                            ) : d.send_status === "Pending" ? (
                                                                <FontAwesomeIcon icon={faHourglassStart} className="text-lg text-gray-400 ml-2" />
                                                            ) : (
                                                                ""
                                                            )}
                                                        </>
                                                    ) : (
                                                        ""
                                                    )}
                                                    {(parseInt(d?.bot_flow_id) > 0 || parseInt(d?.rule_id) > 0) && (
                                                        <button
                                                            type="button"
                                                            className="ml-2 text-orange-600 hover:text-orange-700 cursor-pointer"
                                                            title="View bot flow details"
                                                            onClick={() => {
                                                                const flowName = d.bot_flow?.name || `Flow #${d.bot_flow_id}`;
                                                                const parentName = d.bot_flow?.parent?.name;
                                                                const ruleName = d.bot_flow_rule?.bot_flow?.name;
                                                                const ruleId = d.rule_id;

                                                                let message = `**Bot Flow:** ${flowName}\n**Bot Flow ID:** ${d.bot_flow_id}`;
                                                                if (parentName) {
                                                                    message += `\n**Parent Flow:** ${parentName}\n**Parent Flow ID:** ${d.bot_flow?.parent?.id}`;
                                                                }
                                                                if (ruleId > 0 && ruleName) {
                                                                    message += `\n\n**Triggered by Rule:** ${ruleName}\n**Rule ID:** ${ruleId}`;
                                                                }

                                                                alert(message);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faRobot} className="text-sm" />
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-xs text-gray-400">{moment(d.timestamp).format("DD MMM YY HH:mm")}</span>
                                                    {!!current_staff?.show_dev_stuff && (
                                                        <button type="button" className="text-red-500 hover:text-red-700" title="Delete message" onClick={() => handleDeleteMessage(d.id)}>
                                                            <FontAwesomeIcon icon={faTrash} className="text-sm" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-left flex-shrink-0">
                                                <ProfilePicture id={parseInt(d.from)} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-10">
                                <p className="text-gray-500">{messages.length === 0 ? "No chat history found" : "No messages match your filters"}</p>
                                {(platformFilter.length > 0 || searchTerm) && (
                                    <button
                                        type="button"
                                        className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                                        onClick={() => {
                                            setPlatformFilter([]);
                                            setSearchTerm("");
                                        }}
                                    >
                                        Clear filters
                                    </button>
                                )}
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </>
            )}
        </div>
    );
}
